// Functions for recording information about text that is recorded masked. It starts with a "non-character" marker
// (\uffff) to mark it as masked text, and preserves only the following properties of the original text:
// - Length
// - Line lengths
// - Leading/trailing whitespace length
//
// Length must be preserved to avoid breaking other features like text selection. Leading/trailing whitespace must be
// preserved because DOM text nodes often include large amounts of semantically meaningless whitespace that, if displayed
// with printable characters, looks nothing like the source. And line-lengths must be preserved because many sites use
// styles that preserve line structure for simplified formatting.
//
// It has the following format:
//   \uffff(line-len)(:line-len)*( lead-len trail-len)?

// IE and Phantom don't support String.repeat().
// This code should only be used in replay, but we still need this to pass tests.
interface ES6String extends String {
  repeat(count: number): string;
}

const nonWsExp = new RegExp('[^\\s]');
const wsTrailExp = new RegExp('[\\s]*$');
const space: ES6String = ' ' as any;
const asterisk: ES6String = '*' as any;

const repeat =
  'repeat' in String.prototype
    ? (ch: ES6String, len: number) => ch.repeat(len)
    : (ch: ES6String, len: number) => {
        let s = '';
        for (let i = 0; i < len; i++) {
          s += ch;
        }
        return s;
      };

export function wasMasked(text: string): boolean {
  return text[0] === '\uffff';
}

export function maskText(text: string): string {
  // Compute lead/trail whitespace lengths.
  let groups = nonWsExp.exec(text);
  if (!groups) {
    // All whitespace.
    return text;
  }
  const lead = groups.index;
  groups = wsTrailExp.exec(text);
  const trail = groups ? text.length - groups.index : 0;

  let masked = '\uffff';
  const lines = text.slice(lead, text.length - trail).split(/\r\n?|\n/g);
  for (let i = 0; i < lines.length; i++) {
    masked += `${lines[i]!.length}`;
    if (i != lines.length - 1) {
      masked += ':';
    }
  }

  if (lead || trail) {
    masked += ` ${lead} ${trail}`;
  }
  return masked;
}

export function renderMasked(text: string): string;
export function renderMasked(text: string | null): string | null;
export function renderMasked(text: string | null): string | null {
  if (!text || !wasMasked(text)) {
    return text;
  }

  const parts = text.slice(1).split(' ');
  const lead = parts.length >= 2 ? parseInt(parts[1]!, 10) : 0;
  const trail = parts.length >= 3 ? parseInt(parts[2]!, 10) : 0;

  // Lead space.
  let masked = repeat(space, lead);

  // Words.
  const lines = parts[0]!.split(':');
  for (let i = 0; i < lines.length; i++) {
    const len = parseInt(lines[i]!, 10);
    for (let j = 0; j < len; ) {
      const wordLen = Math.min(4, len - j);
      masked += repeat(asterisk, wordLen);

      j += wordLen;
      if (j == len - 1) {
        masked += '*'; // Last character must be non-whitespace.
        j++;
      } else if (j < len) {
        masked += ' '; // Word separator.
        j++;
      }
    }
    if (i < lines.length - 1) {
      masked += '\n';
    }
  }

  // Trail space.
  masked += repeat(space, trail);
  return masked;
}
